import React, { useState, useEffect, useCallback } from 'react';
import {
  Sliders,
  Radio,
  Plus,
  Edit2,
  Trash2,
  Eye,
  CheckCircle,
  X,
  Save,
  ArrowRight,
  Code2,
  AlertCircle
} from 'lucide-react';
import { fetchMnoConfigs, saveMnoConfig, deleteMnoConfig } from '../services/api';
import { useTelemetry } from '../context/TelemetryContext';

export default function MnoConfigsView(props) {
  const telemetry = useTelemetry();
  const forceMock = props.forceMock ?? telemetry.forceMock;
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inspectingConfig, setInspectingConfig] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [notification, setNotification] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMnoConfigs(forceMock);
      setConfigs(data || []);
    } catch (e) {
      console.error('Error fetching MNO configs:', e);
    } finally {
      setLoading(false);
    }
  }, [forceMock]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const showToast = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3500);
  };

  // Open Edit Modal with cloned config
  const handleEdit = (cfg) => {
    setEditingConfig(JSON.parse(JSON.stringify(cfg)));
    setIsCreatingNew(false);
  };

  // Open Create New Modal with template
  const handleCreate = () => {
    setEditingConfig({
      code: '',
      displayName: '',
      protocol: 'XML_REST',
      enabled: true,
      country: 'UG',
      fieldMappings: {
        msisdnField: 'msisdn',
        userInputFields: ['subscriberInput', 'input'],
        sessionIdField: 'sessionId',
        transactionIdField: 'transactionId',
        newRequestField: 'newRequest',
        newRequestLoginValue: '1',
      },
      shortcodeRoutes: [
        { shortcode: '*185#', targetRoute: 'HELLO_MONEY' }
      ],
      stateRules: {
        continuationState: 'FC',
        terminalState: 'FB',
        terminalActions: ['end', 'close', 'FB'],
      }
    });
    setIsCreatingNew(true);
  };

  // Save / Update to MongoDB
  const handleSave = async () => {
    if (!editingConfig.code?.trim() || !editingConfig.displayName?.trim()) {
      showToast('Please provide both MNO Code and Display Name', 'error');
      return;
    }

    setSaving(true);
    try {
      await saveMnoConfig(editingConfig);
      showToast(isCreatingNew ? `MNO ${editingConfig.code} created successfully!` : `MNO ${editingConfig.code} updated in MongoDB!`);
      setEditingConfig(null);
      await loadConfigs();
    } catch (e) {
      showToast(`Error saving configuration: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Delete MNO
  const handleDelete = async (cfg) => {
    if (!window.confirm(`Are you sure you want to delete MNO configuration for ${cfg.displayName} (${cfg.code})?`)) {
      return;
    }

    try {
      if (cfg.id) {
        await deleteMnoConfig(cfg.id);
      }
      showToast(`MNO ${cfg.code} deleted from database.`);
      await loadConfigs();
    } catch (e) {
      showToast(`Error deleting MNO: ${e.message}`, 'error');
    }
  };

  // Field mapping input helper (comma-separated array to list)
  const handleUserInputFieldsChange = (val) => {
    const list = val.split(',').map(s => s.trim()).filter(Boolean);
    setEditingConfig(prev => ({
      ...prev,
      fieldMappings: {
        ...prev.fieldMappings,
        userInputFields: list
      }
    }));
  };

  // Add Shortcode Route row
  const addShortcodeRoute = () => {
    setEditingConfig(prev => ({
      ...prev,
      shortcodeRoutes: [...(prev.shortcodeRoutes || []), { shortcode: '*123#', targetRoute: 'HELLO_MONEY' }]
    }));
  };

  // Remove Shortcode Route row
  const removeShortcodeRoute = (index) => {
    setEditingConfig(prev => ({
      ...prev,
      shortcodeRoutes: prev.shortcodeRoutes.filter((_, i) => i !== index)
    }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* Toast Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: '24px',
          right: '32px',
          background: notification.type === 'error' ? '#EF4444' : '#10B981',
          color: '#FFFFFF',
          padding: '12px 20px',
          borderRadius: 'var(--radius-md)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontSize: '13px',
          fontWeight: '700',
          zIndex: 300,
        }}>
          {notification.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
          <span>{notification.msg}</span>
        </div>
      )}

      {/* Header & Create Action */}
      <div className="glass-card" style={{ padding: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Sliders size={18} color="var(--accent-orange)" />
            <h2 style={{ fontSize: '18px', fontWeight: '800' }}>MNO Gateway & Dynamic Routing Configurations</h2>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Manage telco payload schemas, shortcode dispatchers, and state machine rules saved in MongoDB (<code>mno_configs</code>)
          </p>
        </div>

        <button className="btn-primary" onClick={handleCreate}>
          <Plus size={16} />
          <span>Add New MNO Configuration</span>
        </button>
      </div>

      {/* Configurations Grid */}
      {loading ? (
        <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading MNO configurations from MongoDB...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '20px' }}>
          {configs.map((cfg, idx) => (
            <div key={idx} className="glass-card" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
              
              {/* Card Header & Actions */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '10px',
                      background: cfg.code === 'AIRTEL' ? 'rgba(248, 65, 34, 0.12)' : cfg.code === 'MTN' ? 'rgba(245, 158, 11, 0.12)' : 'rgba(15, 23, 42, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Radio size={18} color={cfg.code === 'AIRTEL' ? '#F84122' : cfg.code === 'MTN' ? '#F59E0B' : '#0F172A'} />
                    </div>
                    <div>
                      <h3 style={{ fontSize: '16px', fontWeight: '800', color: 'var(--text-primary)' }}>
                        {cfg.displayName}
                      </h3>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                        Code: {cfg.code} | {cfg.protocol}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    onClick={() => setInspectingConfig(cfg)}
                    style={{ background: '#F1F5F9', border: '1px solid var(--border-subtle)', borderRadius: '6px', padding: '6px', color: 'var(--text-secondary)', cursor: 'pointer' }}
                    title="View Raw JSON Document"
                  >
                    <Eye size={14} />
                  </button>
                  <button
                    onClick={() => handleEdit(cfg)}
                    style={{ background: 'rgba(248, 65, 34, 0.1)', border: '1px solid rgba(248, 65, 34, 0.25)', borderRadius: '6px', padding: '6px', color: 'var(--accent-orange)', cursor: 'pointer' }}
                    title="Edit MNO Configuration"
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(cfg)}
                    style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.25)', borderRadius: '6px', padding: '6px', color: 'var(--accent-rose)', cursor: 'pointer' }}
                    title="Delete MNO Configuration"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {/* Status & Country Info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                <span style={{
                  color: cfg.enabled !== false ? 'var(--accent-emerald)' : 'var(--text-muted)',
                  background: cfg.enabled !== false ? 'rgba(16, 185, 129, 0.12)' : '#F1F5F9',
                  padding: '3px 8px',
                  borderRadius: '999px',
                  fontWeight: '700'
                }}>
                  ● {cfg.enabled !== false ? 'Enabled in Router' : 'Disabled'}
                </span>
                {cfg.country && (
                  <span style={{ color: 'var(--text-muted)' }}>Country: <strong>{cfg.country}</strong></span>
                )}
              </div>

              {/* Dynamic Field Extractors */}
              <div style={{ background: '#F8FAFC', padding: '14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px', fontWeight: '700' }}>
                  <Code2 size={13} color="var(--accent-orange)" />
                  <span>Payload Field Mappings</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>MSISDN: </span>
                    <strong style={{ color: '#0F172A', fontFamily: 'var(--font-mono)' }}>{cfg.fieldMappings?.msisdnField || 'msisdn'}</strong>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>SessionId: </span>
                    <strong style={{ color: '#0F172A', fontFamily: 'var(--font-mono)' }}>{cfg.fieldMappings?.sessionIdField || 'sessionId'}</strong>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Input Fallback: </span>
                    <strong style={{ color: 'var(--accent-orange)', fontFamily: 'var(--font-mono)' }}>
                      {cfg.fieldMappings?.userInputFields?.join(' → ') || 'input'}
                    </strong>
                  </div>
                  {cfg.fieldMappings?.transactionIdField && (
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>TxnId: </span>
                      <strong style={{ color: '#0F172A', fontFamily: 'var(--font-mono)' }}>{cfg.fieldMappings.transactionIdField}</strong>
                    </div>
                  )}
                  {cfg.fieldMappings?.newRequestField && (
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>NewReq: </span>
                      <strong style={{ color: '#0F172A', fontFamily: 'var(--font-mono)' }}>{cfg.fieldMappings.newRequestField} = {cfg.fieldMappings.newRequestLoginValue || '1'}</strong>
                    </div>
                  )}
                </div>
              </div>

              {/* Shortcode Routing Table */}
              <div>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase' }}>
                  Shortcode Target Routes
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
                  {cfg.shortcodeRoutes && cfg.shortcodeRoutes.length > 0 ? (
                    cfg.shortcodeRoutes.map((route, i) => (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '7px 10px',
                          background: '#F8FAFC',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: '6px',
                          fontSize: '12px'
                        }}
                      >
                        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: '700', color: 'var(--text-primary)' }}>
                          {route.shortcode}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--accent-orange)', fontWeight: '700', fontSize: '11px' }}>
                          <ArrowRight size={12} />
                          <span>{route.targetRoute}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                      Default route (HELLO_MONEY)
                    </div>
                  )}
                </div>
              </div>

            </div>
          ))}
        </div>
      )}

      {/* ========================================================================= */}
      {/* 1. EDIT / CREATE MNO MODAL                                               */}
      {/* ========================================================================= */}
      {editingConfig && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 200,
          padding: '20px'
        }}>
          <div className="glass-card" style={{
            width: '100%',
            maxWidth: '680px',
            maxHeight: '90vh',
            background: '#FFFFFF',
            border: '1px solid var(--border-medium)',
            borderRadius: 'var(--radius-lg)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.2)'
          }}>
            
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid var(--border-subtle)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#FFFFFF'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #F84122 0%, #E65100 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Edit2 size={16} color="#FFFFFF" />
                </div>
                <h3 style={{ fontSize: '17px', fontWeight: '800', color: 'var(--text-primary)' }}>
                  {isCreatingNew ? 'Create New MNO Gateway' : `Edit Configuration: ${editingConfig.code}`}
                </h3>
              </div>
              <button
                onClick={() => setEditingConfig(null)}
                style={{ background: '#F1F5F9', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', borderRadius: '6px', padding: '6px' }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Scrollable Form */}
            <div style={{ padding: '24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '18px' }}>
              
              {/* Row 1: Code & Display Name */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase' }}>
                    MNO Code *
                  </label>
                  <input
                    type="text"
                    value={editingConfig.code || ''}
                    onChange={e => setEditingConfig({ ...editingConfig, code: e.target.value.toUpperCase() })}
                    placeholder="e.g. SAFARICOM"
                    disabled={!isCreatingNew && !!editingConfig.id}
                    style={{
                      width: '100%',
                      marginTop: '4px',
                      background: '#F8F9FA',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      color: 'var(--text-primary)',
                      fontSize: '13px',
                      fontFamily: 'var(--font-mono)'
                    }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase' }}>
                    Display Name *
                  </label>
                  <input
                    type="text"
                    value={editingConfig.displayName || ''}
                    onChange={e => setEditingConfig({ ...editingConfig, displayName: e.target.value })}
                    placeholder="e.g. Safaricom Kenya"
                    style={{
                      width: '100%',
                      marginTop: '4px',
                      background: '#F8F9FA',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      color: 'var(--text-primary)',
                      fontSize: '13px'
                    }}
                  />
                </div>
              </div>

              {/* Row 2: Protocol & Country & Enabled */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase' }}>
                    Transport Protocol
                  </label>
                  <select
                    className="custom-select"
                    style={{ width: '100%', marginTop: '4px' }}
                    value={editingConfig.protocol || 'XML_REST'}
                    onChange={e => setEditingConfig({ ...editingConfig, protocol: e.target.value })}
                  >
                    <option value="XML_REST">XML REST (e.g. Airtel)</option>
                    <option value="XML_RPC">XML-RPC (e.g. MTN)</option>
                    <option value="JSON_REST">JSON REST (e.g. Vodacom)</option>
                    <option value="SOAP">SOAP WebService</option>
                    <option value="SMPP">SMPP Protocol</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase' }}>
                    Country Code
                  </label>
                  <input
                    type="text"
                    value={editingConfig.country || 'UG'}
                    onChange={e => setEditingConfig({ ...editingConfig, country: e.target.value.toUpperCase() })}
                    placeholder="e.g. UG, ZA, KE"
                    style={{
                      width: '100%',
                      marginTop: '4px',
                      background: '#F8F9FA',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      color: 'var(--text-primary)',
                      fontSize: '13px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase' }}>
                    Router Status
                  </label>
                  <select
                    className="custom-select"
                    style={{ width: '100%', marginTop: '4px' }}
                    value={editingConfig.enabled !== false ? 'true' : 'false'}
                    onChange={e => setEditingConfig({ ...editingConfig, enabled: e.target.value === 'true' })}
                  >
                    <option value="true">Enabled (Active)</option>
                    <option value="false">Disabled (Paused)</option>
                  </select>
                </div>
              </div>

              {/* Dynamic Payload Field Mappings */}
              <div style={{ background: '#F8FAFC', padding: '16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <span style={{ fontSize: '12px', fontWeight: '800', color: 'var(--accent-orange)' }}>
                  Dynamic Payload Extraction Field Names
                </span>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600' }}>MSISDN Key (e.g. MSISDN, msisdn)</label>
                    <input
                      type="text"
                      value={editingConfig.fieldMappings?.msisdnField || ''}
                      onChange={e => setEditingConfig({ ...editingConfig, fieldMappings: { ...editingConfig.fieldMappings, msisdnField: e.target.value } })}
                      style={{ width: '100%', marginTop: '2px', background: '#FFFFFF', border: '1px solid var(--border-medium)', borderRadius: '6px', padding: '6px 10px', color: 'var(--text-primary)', fontSize: '12px', fontFamily: 'var(--font-mono)' }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600' }}>SessionId Key (e.g. SessionId, sessionId)</label>
                    <input
                      type="text"
                      value={editingConfig.fieldMappings?.sessionIdField || ''}
                      onChange={e => setEditingConfig({ ...editingConfig, fieldMappings: { ...editingConfig.fieldMappings, sessionIdField: e.target.value } })}
                      style={{ width: '100%', marginTop: '2px', background: '#FFFFFF', border: '1px solid var(--border-medium)', borderRadius: '6px', padding: '6px 10px', color: 'var(--text-primary)', fontSize: '12px', fontFamily: 'var(--font-mono)' }}
                    />
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600' }}>
                      User Input Fallback Keys (comma-separated, evaluated in order)
                    </label>
                    <input
                      type="text"
                      value={editingConfig.fieldMappings?.userInputFields?.join(', ') || ''}
                      onChange={e => handleUserInputFieldsChange(e.target.value)}
                      placeholder="e.g. subscriberInput, input, USSDRequestString"
                      style={{ width: '100%', marginTop: '2px', background: '#FFFFFF', border: '1px solid var(--border-medium)', borderRadius: '6px', padding: '6px 10px', color: 'var(--accent-orange)', fontSize: '12px', fontFamily: 'var(--font-mono)' }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600' }}>Transaction ID Key</label>
                    <input
                      type="text"
                      value={editingConfig.fieldMappings?.transactionIdField || ''}
                      onChange={e => setEditingConfig({ ...editingConfig, fieldMappings: { ...editingConfig.fieldMappings, transactionIdField: e.target.value } })}
                      placeholder="e.g. transactionId"
                      style={{ width: '100%', marginTop: '2px', background: '#FFFFFF', border: '1px solid var(--border-medium)', borderRadius: '6px', padding: '6px 10px', color: 'var(--text-primary)', fontSize: '12px', fontFamily: 'var(--font-mono)' }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600' }}>Transaction Time Key</label>
                    <input
                      type="text"
                      value={editingConfig.fieldMappings?.transactionTimeField || ''}
                      onChange={e => setEditingConfig({ ...editingConfig, fieldMappings: { ...editingConfig.fieldMappings, transactionTimeField: e.target.value } })}
                      placeholder="e.g. transactionTime"
                      style={{ width: '100%', marginTop: '2px', background: '#FFFFFF', border: '1px solid var(--border-medium)', borderRadius: '6px', padding: '6px 10px', color: 'var(--text-primary)', fontSize: '12px', fontFamily: 'var(--font-mono)' }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600' }}>Service / Shortcode Key</label>
                    <input
                      type="text"
                      value={editingConfig.fieldMappings?.serviceCodeField || ''}
                      onChange={e => setEditingConfig({ ...editingConfig, fieldMappings: { ...editingConfig.fieldMappings, serviceCodeField: e.target.value } })}
                      placeholder="e.g. serviceCode"
                      style={{ width: '100%', marginTop: '2px', background: '#FFFFFF', border: '1px solid var(--border-medium)', borderRadius: '6px', padding: '6px 10px', color: 'var(--text-primary)', fontSize: '12px', fontFamily: 'var(--font-mono)' }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600' }}>New Request Flag Key</label>
                    <input
                      type="text"
                      value={editingConfig.fieldMappings?.newRequestField || ''}
                      onChange={e => setEditingConfig({ ...editingConfig, fieldMappings: { ...editingConfig.fieldMappings, newRequestField: e.target.value } })}
                      placeholder="e.g. newRequest"
                      style={{ width: '100%', marginTop: '2px', background: '#FFFFFF', border: '1px solid var(--border-medium)', borderRadius: '6px', padding: '6px 10px', color: 'var(--text-primary)', fontSize: '12px', fontFamily: 'var(--font-mono)' }}
                    />
                  </div>
                </div>
              </div>

              {/* State Machine Rules */}
              <div style={{ background: '#F8FAFC', padding: '16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <span style={{ fontSize: '12px', fontWeight: '800', color: 'var(--accent-emerald)' }}>
                  State Machine Protocol Codes
                </span>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600' }}>Continuation State Code (e.g. FC)</label>
                    <input
                      type="text"
                      value={editingConfig.stateRules?.continuationState || 'FC'}
                      onChange={e => setEditingConfig({ ...editingConfig, stateRules: { ...editingConfig.stateRules, continuationState: e.target.value } })}
                      style={{ width: '100%', marginTop: '2px', background: '#FFFFFF', border: '1px solid var(--border-medium)', borderRadius: '6px', padding: '6px 10px', color: 'var(--text-primary)', fontSize: '12px', fontFamily: 'var(--font-mono)' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600' }}>Terminal State Code (e.g. FB)</label>
                    <input
                      type="text"
                      value={editingConfig.stateRules?.terminalState || 'FB'}
                      onChange={e => setEditingConfig({ ...editingConfig, stateRules: { ...editingConfig.stateRules, terminalState: e.target.value } })}
                      style={{ width: '100%', marginTop: '2px', background: '#FFFFFF', border: '1px solid var(--border-medium)', borderRadius: '6px', padding: '6px 10px', color: 'var(--text-primary)', fontSize: '12px', fontFamily: 'var(--font-mono)' }}
                    />
                  </div>
                </div>
              </div>

              {/* Shortcode Routing Table */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', fontWeight: '800', color: 'var(--text-primary)' }}>
                    Shortcode Routing Table
                  </span>
                  <button
                    type="button"
                    onClick={addShortcodeRoute}
                    style={{ background: 'transparent', border: 'none', color: 'var(--accent-orange)', fontSize: '12px', cursor: 'pointer', fontWeight: '700' }}
                  >
                    + Add Shortcode Route
                  </button>
                </div>

                {editingConfig.shortcodeRoutes?.map((route, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.8fr auto', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={route.shortcode || ''}
                      onChange={e => {
                        const newRoutes = [...editingConfig.shortcodeRoutes];
                        newRoutes[i].shortcode = e.target.value;
                        setEditingConfig({ ...editingConfig, shortcodeRoutes: newRoutes });
                      }}
                      placeholder="e.g. *185#"
                      style={{ background: '#F8F9FA', border: '1px solid var(--border-medium)', borderRadius: '6px', padding: '6px 10px', color: 'var(--text-primary)', fontSize: '12px', fontFamily: 'var(--font-mono)' }}
                    />
                    <select
                      className="custom-select"
                      value={route.targetRoute || 'HELLO_MONEY'}
                      onChange={e => {
                        const newRoutes = [...editingConfig.shortcodeRoutes];
                        newRoutes[i].targetRoute = e.target.value;
                        setEditingConfig({ ...editingConfig, shortcodeRoutes: newRoutes });
                      }}
                    >
                      <option value="HELLO_MONEY">Hello Money (Core Banking)</option>
                      <option value="DIGITAL_WALLET">Digital Wallet Gateway</option>
                      <option value="USSD_WRAPPER">USSD Wrapper</option>
                      <option value="proxy">Legacy Proxy</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => removeShortcodeRoute(i)}
                      style={{ background: 'transparent', border: 'none', color: 'var(--accent-rose)', cursor: 'pointer', padding: '4px' }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>

            </div>

            {/* Modal Actions */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--border-subtle)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              background: '#F8FAFC'
            }}>
              <button className="btn-secondary" onClick={() => setEditingConfig(null)} disabled={saving}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                <Save size={14} />
                <span>{saving ? 'Saving...' : 'Save to MongoDB'}</span>
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 2. INSPECT RAW JSON DOCUMENT MODAL                                       */}
      {/* ========================================================================= */}
      {inspectingConfig && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 200,
          padding: '20px'
        }}>
          <div className="glass-card" style={{
            width: '100%',
            maxWidth: '620px',
            background: '#FFFFFF',
            border: '1px solid var(--border-medium)',
            borderRadius: 'var(--radius-lg)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.2)'
          }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Code2 size={16} color="var(--accent-orange)" />
                <h3 style={{ fontSize: '15px', fontWeight: '800', color: 'var(--text-primary)' }}>MongoDB Document: {inspectingConfig.code}</h3>
              </div>
              <button onClick={() => setInspectingConfig(null)} style={{ background: '#F1F5F9', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', borderRadius: '6px', padding: '6px' }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ padding: '16px', maxHeight: '480px', overflowY: 'auto' }}>
              <pre style={{
                background: '#0B0F19',
                padding: '14px',
                borderRadius: '8px',
                fontSize: '12px',
                color: '#34D399',
                fontFamily: 'var(--font-mono)',
                overflowX: 'auto',
                lineHeight: '1.5'
              }}>
                {JSON.stringify(inspectingConfig, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
